import { useState, useEffect } from 'react';
import { preloadCardImages, type PreloadProgress } from '../../utils/imagePreloader';

interface CardPreloaderProps {
  onComplete: () => void;
}

export function CardPreloader({ onComplete }: CardPreloaderProps) {
  const [progress, setProgress] = useState<PreloadProgress>({
    loaded: 0,
    total: 78,
    percentage: 0
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setError(null);
        
        await preloadCardImages((progressData) => {
          setProgress(progressData);
        });
        
        // Small delay to show 100% completion
        setTimeout(() => {
          onComplete();
        }, 500);
        
      } catch (err) {
        console.error('Preload failed:', err);
        setError('Failed to load card images');
        // Still call onComplete to allow app to continue
        setTimeout(onComplete, 1000);
      }
    };
    
    load();
  }, [onComplete]);

  if (error) {
    return (
      <div className="fixed inset-0 bg-dark-900 flex items-center justify-center z-50">
        <div className="text-center max-w-md px-4">
          <div className="mb-6">
            <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">⚠️</span>
            </div>
            <h2 className="text-xl font-bold text-white mb-2">
              Loading Error
            </h2>
            <p className="text-gray-400 text-sm">
              {error}. The app will continue with on-demand loading.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-dark-900 flex items-center justify-center z-50">
      <div className="text-center max-w-md px-4">
        {/* Logo/Icon */}
        <div className="mb-6">
          <div className="w-20 h-20 bg-primary-500/10 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <span className="text-3xl">🎴</span>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">
            Preparing Your Deck
          </h2>
          <p className="text-gray-400">
            Loading {progress.loaded} of {progress.total} cards...
          </p>
        </div>
        
        {/* Progress Bar */}
        <div className="mb-6">
          <div className="w-full h-3 bg-dark-700 rounded-full overflow-hidden mb-2">
            <div 
              className="h-full bg-gradient-to-r from-primary-500 to-primary-400 transition-all duration-300 ease-out"
              style={{ width: `${progress.percentage}%` }}
            />
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-400">
              {progress.percentage}% complete
            </span>
            <span className="text-primary-500 font-semibold">
              {progress.loaded}/{progress.total}
            </span>
          </div>
        </div>
        
        {/* Current Image Info */}
        {progress.currentImage && (
          <div className="text-xs text-gray-500 mb-4">
            Loading: {progress.currentImage.split('/').pop()?.replace('.webp', '').replace(/_/g, ' ')}
          </div>
        )}
        
        {/* Loading Animation */}
        <div className="flex justify-center space-x-1 mb-6">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-2 h-2 bg-primary-500 rounded-full animate-bounce"
              style={{
                animationDelay: `${i * 0.2}s`,
                animationDuration: '1s'
              }}
            />
          ))}
        </div>
        
        {/* Tips */}
        <div className="text-xs text-gray-500 space-y-1">
          <p>✨ This happens only once</p>
          <p>🚀 Future card loads will be instant</p>
          <p>📱 Works offline after loading</p>
        </div>
      </div>
    </div>
  );
}